import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";
import { useTranslation } from "react-i18next";
import axios from "axios";
import cn from "classnames";
import {
    Button,
    ButtonToolbar,
    Checkbox,
    Form,
    FormInstance,
    Heading,
    HStack,
    Stack,
    IconButton,
    Message,
    Radio,
    RadioGroup,
    SelectPicker,
    Table,
    Text,
    toaster,
} from "rsuite";
import { TableInstance } from "rsuite/esm/Table";
import { SortType } from "rsuite-table";
import SaveIcon from "@rsuite/icons/Save";
import EditIcon from "@rsuite/icons/Edit";

import i18n from "utils/i18n";
import constants from "utils/constants";

import { getSorted, Paths } from "utils/sort-halper";
import { getModalTableHeight } from "utils/measurement-helper";

import { getErrorMessage } from "api/defaults";
import {
    createMailboxes,
    loadDefaultMailboxes,
} from "api/environments/tenants/mailboxes-api";

import { AppDispatch, RootState } from "store/store";
import { getActiveEnvironmentId } from "store/environments/environment-selectors";
import {
    getTenantDisplayName,
    getTenantId,
    getTenantMoniker,
} from "store/environments/tenants/tenants-selectors";

import { setErrorResponseMessageForModal } from "store/common/initialization/initialization-actions";
import {
    setModalLoaderVisibility,
    getIsModalLoaderVisible,
} from "store/common/modal-window/modal-window-slices";

import {
    Mailbox,
    MailboxDefault,
    MailboxCreationType,
} from "store/environments/tenants/mailboxes/mailboxes-types";
import { MailboxesWorkflow } from "store/workflows/workflows-types";

import ModalContainer from "components/Common/Modal/ModalContainer";
import EditableCell from "components/Common/EditableCell/EditableCell";

import { formModel, ocrMethods } from "../mailboxes-helpers";

import "./Create.less";

const useAppDispatch = () => useDispatch<AppDispatch>();

interface Props {
    title?: string;
    open: boolean;

    setOpen?: (open: boolean) => void;
    callback?: (workflow: MailboxesWorkflow) => void;
}

type TableItem<T> = T & {
    editing: boolean | null;
    checked: boolean;
    errors?: Record<string, string> | null;
};

type FormItem<T> = T & {
    editing: boolean;
};

export type TableColumn = Paths<Mailbox>;

const columns: Record<
    keyof Omit<Mailbox, "id" | "creator" | "creationDate">,
    TableColumn
> = {
    name: "name",
    address: "address",
    disabled: "disabled",
    ocrMethod: "ocrMethod",
    company: "company.remoteId",
};

const Create: React.FC<Props> = ({
    title = i18n.t("tenantConfig.captureMailboxes.mailboxCreation.title"),
    open,
    setOpen,
    callback,
}: Props) => {
    const cancelTokenSource = axios.CancelToken.source();
    const dispatch = useAppDispatch();

    const { t } = useTranslation();

    const activeEnvironmentId = useSelector((state: RootState): string =>
        getActiveEnvironmentId(state)
    );

    const activeTenantId = useSelector((state: RootState): string =>
        getTenantId(state)
    );

    const activeTenantMoniker = useSelector((state: RootState): string =>
        getTenantMoniker(state)
    );

    const activeTenantName = useSelector((state: RootState): string =>
        getTenantDisplayName(state)
    );

    const isLoading = useSelector((state: RootState): boolean =>
        getIsModalLoaderVisible(state)
    );

    const tableBodyRef = useRef<TableInstance<any, any> | null>(null);
    const formRef = useRef<FormInstance<Record<string, any>> | null>(null);

    const [dataFetched, setDataFetched] = useState<boolean>(false);

    const [selectedMailboxes, setSelectedMailboxes] = useImmer<Mailbox[]>([]);

    const [genericMailboxFormItem, setGenericMailboxFormItem] = useImmer<
        FormItem<MailboxDefault>
    >({} as FormItem<MailboxDefault>);

    const [selectedType, setSelectedType] = useState<MailboxCreationType>(
        MailboxCreationType.Generic
    );

    const [companyMailboxesTableData, setCompanyMailboxesTableData] = useImmer<
        TableItem<MailboxDefault>[]
    >([]);
    const [sortColumn, setSortColumn] = useState<TableColumn>(columns.company);
    const [sortType, setSortType] = useState<SortType>("desc");

    const [companyMailboxesAllChecked, setCompanyMailboxesAllChecked] =
        useState<boolean>(true);

    const [createPermitted, setCreatePermitted] = useState<boolean>(true);

    const companyMailboxesIndeterminate = companyMailboxesTableData.some(
        (ti) => !ti.checked
    );

    const headerHeight = 40;
    const rowHeight = 46;

    const tableHeight = getModalTableHeight(companyMailboxesTableData.length);

    const fetchData = () => {
        dispatch(setModalLoaderVisibility(true));
        dispatch(setErrorResponseMessageForModal(""));

        Promise.all([fetchDefaultMailboxes()])
            .then((value) => {
                if (value.some((v) => typeof v == "string")) {
                    const errors = new Set<string>(
                        value.filter((v) => typeof v == "string")
                    );

                    const message = [...errors].join(". ");
                    dispatch(setErrorResponseMessageForModal(message));
                }
            })
            .finally(() => {
                dispatch(setModalLoaderVisibility(false));
                setDataFetched(true);
            });
    };

    const fetchDefaultMailboxes = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadDefaultMailboxes(
                activeEnvironmentId,
                activeTenantId,
                cancelTokenSource
            );

            if (status === constants.statusCode.OK && payload) {
                const companyMailboxes = payload.data.filter(
                    (mailbox) => mailbox.company !== null
                );

                const genericMailbox =
                    payload.data.find((mailbox) => mailbox.company === null) ??
                    null;

                setGenericMailboxFormItem({
                    ...genericMailbox!,
                    editing: false,
                });

                if (sortColumn && sortType) {
                    setCompanyMailboxesTableData(
                        getSorted(
                            [
                                ...companyMailboxes.map((mailbox) => ({
                                    ...mailbox,
                                    editing: false,
                                    checked: mailbox.exists,
                                })),
                            ],
                            sortColumn,
                            sortType
                        )
                    );
                } else {
                    setCompanyMailboxesTableData([
                        ...companyMailboxes.map((mailbox) => ({
                            ...mailbox,
                            editing: false,
                            checked: mailbox.exists,
                        })),
                    ]);
                }

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const handleModalClose = () => {
        cancelTokenSource.cancel();

        setSelectedType(MailboxCreationType.Generic);
        setCompanyMailboxesAllChecked(true);
        setDataFetched(false);

        setSortColumn(columns.company);
        setSortType("desc");

        setOpen?.(false);
    };

    const handleTypeChange = (value: MailboxCreationType) => {
        if (
            [MailboxCreationType.Generic, MailboxCreationType.All].includes(
                value
            )
        ) {
            formRef?.current?.check();
        } else {
            formRef?.current?.cleanErrors();
        }

        setSelectedType(value);
    };

    const validateRow = (rowData: TableItem<Mailbox>) => {
        const result = formModel.check({
            name: rowData.name,
            address: rowData.address,
        });

        return {
            isValid: !result?.name?.hasError && !result?.address?.hasError,
            errors: {
                name: result?.name?.errorMessage ?? "",
                address: result?.address?.errorMessage ?? "",
            },
        };
    };

    const handleEdit = (rowData: TableItem<Mailbox>) => {
        setCompanyMailboxesTableData((draft) => {
            const mailbox = draft.find((mb) => mb.id == rowData.id);
            if (mailbox) {
                mailbox.editing = true;
            }
        });
    };

    const handleSave = (rowData: TableItem<Mailbox>) => {
        const { isValid } = validateRow(rowData);
        if (!isValid) {
            return;
        }

        setCompanyMailboxesTableData((draft) => {
            const mailbox = draft.find((mb) => mb.id == rowData.id);
            if (mailbox) {
                mailbox.editing = !mailbox.editing;
            }
        });

        setSelectedMailboxes((draft) => {
            const mailbox = draft.find((mb) => mb.id == rowData?.id);

            if (mailbox) {
                Object.assign(mailbox, rowData);
            }
        });
    };

    const handleRowDataChange = (rowData: Record<string, any>) => {
        const { isValid, errors } = validateRow(rowData as TableItem<Mailbox>);

        setCompanyMailboxesTableData((draft) => {
            const mailbox = draft.find((mb) => mb.id == rowData.id);
            if (mailbox) {
                Object.assign(mailbox, rowData);

                mailbox.errors = isValid ? null : errors;
            }
        });
    };

    const handleAction = (mailbox: TableItem<Mailbox>) => {
        if (mailbox.editing) {
            handleSave(mailbox);
        } else {
            handleEdit(mailbox);
        }
    };

    const handleSortColumn = (sortColumn: string, sortType: any) => {
        setSortColumn(sortColumn as TableColumn);
        setSortType(sortType);

        setCompanyMailboxesTableData((draft) =>
            getSorted(draft, sortColumn as TableColumn, sortType)
        );
    };

    const handleSubmit = () => {
        if (!createPermitted) {
            return;
        }

        dispatch(setModalLoaderVisibility(true));

        const payload = {
            id: activeTenantId,
            name: activeTenantName,
            moniker: activeTenantMoniker,
            data: selectedMailboxes,
        };

        createMailboxes(activeEnvironmentId, activeTenantId, payload).then(
            ([result, status]) => {
                if (status === constants.statusCode.OK && result) {
                    callback?.(result);
                } else {
                    toaster.push(
                        <Message type="error" showIcon>
                            {getErrorMessage(String(result), status)}
                        </Message>,
                        {
                            duration: constants.alertDurationMsec,
                        }
                    );
                }

                dispatch(setModalLoaderVisibility(false));
                handleModalClose();
            }
        );
    };

    const handleCheck = (checked: boolean, value: MailboxDefault) => {
        if (value.exists) {
            return;
        }
        const updatedMailboxes = companyMailboxesTableData.map((mailbox) =>
            mailbox.id === value.id ? { ...mailbox, checked: checked } : mailbox
        );
        setCompanyMailboxesTableData(updatedMailboxes);
    };

    const handleCheckAll = (checked: boolean) => {
        setCompanyMailboxesAllChecked(checked);

        setCompanyMailboxesTableData((draft) =>
            draft.forEach((mailbox) => {
                if (!mailbox.exists) {
                    mailbox.checked = checked;
                }
            })
        );
    };

    const handleChangeForm = (value: Record<string, any>) => {
        setGenericMailboxFormItem((draft) => {
            return {
                ...draft,
                ...value,
            };
        });
    };

    useEffect(() => {
        if (open && activeEnvironmentId && activeTenantId) {
            fetchData();
        }
    }, [open, activeEnvironmentId, activeTenantId]);

    useEffect(() => {
        setSelectedMailboxes((draft) => {
            const mailboxes = companyMailboxesTableData.filter(
                (m) => !m.exists && !m.editing && m.checked
            );

            draft.length = 0;

            switch (selectedType) {
                case MailboxCreationType.Generic:
                    if (!genericMailboxFormItem.exists) {
                        draft.push({ ...genericMailboxFormItem });
                    }

                    break;
                case MailboxCreationType.Company:
                    draft.push(...mailboxes);

                    break;

                case MailboxCreationType.All:
                    draft.push(...mailboxes);
                    if (!genericMailboxFormItem.exists) {
                        draft.push({ ...genericMailboxFormItem });
                    }

                    break;
            }
        });

        setCompanyMailboxesAllChecked(
            companyMailboxesTableData.some((m) => m.checked)
        );
    }, [genericMailboxFormItem, companyMailboxesTableData, selectedType]);

    useEffect(() => {
        const validated =
            (formRef.current?.check() ?? true) &&
            companyMailboxesTableData.some((item) => validateRow(item).isValid);

        const itemsSaved = companyMailboxesTableData.every(
            (item) => !item.editing
        );

        const selected = selectedMailboxes.length > 0;

        const permitted =
            dataFetched && validated && itemsSaved && selected && !isLoading;

        setCreatePermitted(permitted);
    }, [companyMailboxesTableData, dataFetched, isLoading, selectedMailboxes]);

    const formValue = {
        name: genericMailboxFormItem?.name ?? "",
        address: genericMailboxFormItem?.address ?? "",
        ocrMethod: genericMailboxFormItem?.ocrMethod ?? "",
    };

    const header = <Heading level={1}>{title}</Heading>;
    const footer = (
        <ButtonToolbar>
            <Button
                style={{ height: "2.5rem", width: "5rem" }}
                type="submit"
                disabled={!createPermitted}
                appearance="primary"
                onClick={handleSubmit}
            >
                {t("tenantConfig.captureMailboxes.mailboxCreation.createBtn")}
            </Button>

            <Button
                style={{ height: "2.5rem", width: "5rem" }}
                appearance="ghost"
                onClick={handleModalClose}
            >
                {t("confirmModal.cancel")}
            </Button>
        </ButtonToolbar>
    );

    return (
        <ModalContainer
            className="create-mailboxes__container"
            open={open}
            header={header}
            footer={footer}
            onClose={handleModalClose}
        >
            <RadioGroup
                defaultValue={MailboxCreationType.Generic}
                value={selectedType}
                onChange={(value) =>
                    handleTypeChange(value as MailboxCreationType)
                }
                className="create-mailboxes__radio-group"
            >
                <Radio
                    value={MailboxCreationType.Generic}
                    className="create-mailboxes__radio-item"
                >
                    {t(
                        "tenantConfig.captureMailboxes.mailboxCreation.formType.generic"
                    )}
                </Radio>

                <Radio
                    value={MailboxCreationType.Company}
                    className="create-mailboxes__radio-item"
                >
                    {t(
                        "tenantConfig.captureMailboxes.mailboxCreation.formType.company"
                    )}
                </Radio>

                <Radio
                    value={MailboxCreationType.All}
                    className="create-mailboxes__radio-item"
                >
                    {t(
                        "tenantConfig.captureMailboxes.mailboxCreation.formType.all"
                    )}
                </Radio>
            </RadioGroup>

            <Form
                ref={formRef}
                formValue={formValue}
                model={formModel}
                disabled={genericMailboxFormItem?.exists}
                onChange={handleChangeForm}
                className={
                    selectedType === MailboxCreationType.Company ||
                    genericMailboxFormItem?.exists
                        ? "create-mailboxes--disabled"
                        : ""
                }
            >
                <HStack
                    style={{ margin: "1rem 0" }}
                    justifyContent={"flex-start"}
                    spacing={10}
                >
                    <Stack.Item alignSelf={"flex-start"}>
                        <Form.Group
                            className={cn(
                                "add-modal__form-group",
                                "create-mailboxes__picker"
                            )}
                        >
                            <Form.ControlLabel>
                                {t(`tenantConfig.captureMailboxes.table.ocr`)}
                            </Form.ControlLabel>
                            <Form.Control
                                className={"create-mailboxes__picker"}
                                name="ocrMethod"
                                accepter={SelectPicker}
                                searchable={false}
                                data={ocrMethods}
                                placeholder={t(
                                    "tenantConfig.featureConfigurations.ocrConfigurations.placeholder"
                                )}
                            />
                        </Form.Group>
                    </Stack.Item>

                    <Stack.Item alignSelf={"center"} grow={1}>
                        <Form.Group className="add-modal__form-group">
                            <Form.ControlLabel>
                                {t(`tenantConfig.captureMailboxes.table.name`)}
                            </Form.ControlLabel>

                            <Form.Control name="name" />
                        </Form.Group>
                    </Stack.Item>

                    <Stack.Item alignSelf={"center"} grow={1}>
                        <Form.Group className="add-modal__form-group">
                            <Form.ControlLabel>
                                {t(
                                    `tenantConfig.captureMailboxes.table.address`
                                )}
                            </Form.ControlLabel>

                            <Form.Control name="address" />
                        </Form.Group>
                    </Stack.Item>
                </HStack>
            </Form>

            <Table
                ref={tableBodyRef}
                shouldUpdateScroll={false}
                virtualized={true}
                data={companyMailboxesTableData}
                sortColumn={sortColumn}
                sortType={sortType}
                onSortColumn={handleSortColumn}
                height={tableHeight}
                headerHeight={headerHeight}
                rowHeight={rowHeight}
                className={
                    selectedType === MailboxCreationType.Generic
                        ? "create-mailboxes--disabled"
                        : ""
                }
            >
                <Table.Column flexGrow={1} fixed>
                    <Table.HeaderCell>
                        <Checkbox
                            indeterminate={companyMailboxesIndeterminate}
                            checked={companyMailboxesAllChecked}
                            onChange={(_, checked) => {
                                handleCheckAll(checked);
                            }}
                        />
                    </Table.HeaderCell>
                    <Table.Cell>
                        {(rowData: TableItem<MailboxDefault>) => (
                            <Checkbox
                                checked={rowData.checked}
                                onChange={(_, checked) => {
                                    handleCheck(checked, rowData);
                                }}
                                disabled={rowData.exists}
                            />
                        )}
                    </Table.Cell>
                </Table.Column>

                <Table.Column flexGrow={1.5} minWidth={200}>
                    <Table.HeaderCell>
                        {t(`tenantConfig.captureMailboxes.table.ocr`)}
                    </Table.HeaderCell>
                    <Table.Cell dataKey={columns.ocrMethod}>
                        {(rowData: TableItem<Mailbox>) => (
                            <SelectPicker
                                className={"create-mailboxes__picker"}
                                disabled={!rowData.editing}
                                searchable={false}
                                placeholder={t(
                                    "tenantConfig.featureConfigurations.ocrConfigurations.placeholder"
                                )}
                                data={ocrMethods}
                                value={rowData.ocrMethod}
                                onChange={(value) =>
                                    handleRowDataChange({
                                        ...rowData,
                                        ocrMethod: value,
                                    })
                                }
                            />
                        )}
                    </Table.Cell>
                </Table.Column>

                <Table.Column flexGrow={2} sortable>
                    <Table.HeaderCell>
                        {t(
                            `tenantConfig.captureMailboxes.mailboxCreation.company`
                        )}
                    </Table.HeaderCell>
                    <Table.Cell dataKey={columns.company}>
                        {(rowData: TableItem<MailboxDefault>) => (
                            <Text>{rowData.company?.remoteId}</Text>
                        )}
                    </Table.Cell>
                </Table.Column>

                <Table.Column flexGrow={7} sortable>
                    <Table.HeaderCell>
                        {t(`tenantConfig.captureMailboxes.table.name`)}
                    </Table.HeaderCell>
                    <Table.Cell dataKey={columns.name}>
                        {(rowData: TableItem<MailboxDefault>) => (
                            <EditableCell
                                dataKey={columns.name}
                                rowData={rowData}
                                errorMessage={rowData.errors?.name}
                                onChange={handleRowDataChange}
                            />
                        )}
                    </Table.Cell>
                </Table.Column>

                <Table.Column flexGrow={7} sortable>
                    <Table.HeaderCell>
                        {t(`tenantConfig.captureMailboxes.table.address`)}
                    </Table.HeaderCell>
                    <Table.Cell dataKey={columns.address}>
                        {(rowData: TableItem<MailboxDefault>) => (
                            <EditableCell
                                dataKey={columns.address}
                                rowData={rowData}
                                errorMessage={rowData.errors?.address}
                                onChange={handleRowDataChange}
                            />
                        )}
                    </Table.Cell>
                </Table.Column>

                <Table.Column flexGrow={0.5} minWidth={80}>
                    <Table.HeaderCell>
                        {t(
                            `tenantConfig.captureMailboxes.mailboxCreation.action`
                        )}
                    </Table.HeaderCell>
                    <Table.Cell>
                        {(rowData: TableItem<MailboxDefault>) =>
                            !rowData.exists && (
                                <IconButton
                                    appearance="subtle"
                                    size="sm"
                                    onClick={() => {
                                        handleAction(rowData);
                                    }}
                                    icon={
                                        rowData.editing ? (
                                            <SaveIcon />
                                        ) : (
                                            <EditIcon />
                                        )
                                    }
                                />
                            )
                        }
                    </Table.Cell>
                </Table.Column>
            </Table>
        </ModalContainer>
    );
};

export default Create;
